import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Modal from "../Modal";
import Input from "../Input";
import Button from "../Button";
import AvatarUpload from "../AvatarUpload";
import InputCheckboxUnique from "../InputCheckboxUnique";
import PasswordChecklist from "../PasswordChecklist";

import api from "../../services/api";
import { getUser, setUser } from "../../services/auth";
import { renderError } from "../../helpers/errors";
import "./styles.scss";

/**
 * Modal "Meus dados" — o próprio usuário logado edita nome, e-mail, telefone,
 * foto e (opcionalmente) a senha. Consome as rotas `/profile` da API admin.
 */
export const ModalMyProfile = ({ show, close, onUpdated }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [roleName, setRoleName] = useState('');

    // Foto: `photo` guarda o preview atual (URL da API ou base64 novo);
    // `photoBase64` só é preenchido quando o usuário troca a imagem.
    const [photo, setPhoto] = useState(null);
    const [photoBase64, setPhotoBase64] = useState('');

    const [updatePassword, setUpdatePassword] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isValidPassword, setIsValidPassword] = useState(false);

    useEffect(() => {
        if (show) loadProfile();
    }, [show]);

    useEffect(() => {
        setPassword('');
        setConfirmPassword('');
    }, [updatePassword]);

    const loadProfile = () => {
        setLoading(true);
        setUpdatePassword(false);
        setPassword('');
        setConfirmPassword('');
        setPhotoBase64('');

        api.get('/profile').then(res => {
            const profile = res.data.resource;
            setName(profile.name ?? '');
            setEmail(profile.email ?? '');
            setPhone(profile.phone ?? '');
            setRoleName(profile.role_name ?? 'Sem nível de acesso');
            setPhoto(profile.photo_url ?? null);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    };

    const handlePhotoChange = base64 => {
        setPhotoBase64(base64);
        setPhoto(base64);
    };

    const handleCriteriaChange = criteria => {
        setIsValidPassword(Object.values(criteria).every(Boolean));
    };

    const save = () => {
        if (updatePassword && !isValidPassword) {
            toast.error('A nova senha não atende aos requisitos.');
            return;
        }

        setSaving(true);

        const payload = { name, email, phone };
        if (photoBase64) payload.photo = photoBase64;
        if (updatePassword) {
            payload.password = password;
            payload.password_confirmation = confirmPassword;
        }

        api.put('/profile', payload).then(res => {
            const updated = res.data.resource;

            // Reflete nome/foto imediatamente no header e nas próximas telas.
            const current = getUser() ?? {};
            setUser({
                ...current,
                name: updated.name,
                email: updated.email,
                phone: updated.phone,
                photo_url: updated.photo_url,
            });

            toast.success('Dados atualizados com sucesso');
            if (onUpdated) onUpdated(updated);
            close();
        }).catch(error => {
            renderError(error);
        }).then(() => setSaving(false));
    };

    return (
        <Modal
            title={`Meus dados`}
            show={show}
            close={close}
            footer={
                <Button
                    type={`primary`}
                    size={`small`}
                    text={`Salvar alterações`}
                    svg={`save-white`}
                    action={save}
                    loading={saving || loading}
                />
            }
        >
            <div className="section modal-my-profile">
                <AvatarUpload name={name} photo={photo} onChange={handlePhotoChange} />

                <div className="row">
                    <div className="col-6">
                        <Input type={`text`} label={`Nome`} value={name} change={setName} autoComplete={`new-password`} />
                    </div>
                    <div className="col-6">
                        <Input type={`email`} label={`E-mail`} value={email} change={setEmail} autoComplete={`new-password`} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-6">
                        <Input type={`text`} label={`Telefone`} value={phone} change={setPhone} mask={`convertToPhone`} />
                    </div>
                    <div className="col-6">
                        <Input type={`text`} label={`Nível de acesso`} value={roleName} change={() => {}} disabled={true} />
                    </div>
                </div>

                <InputCheckboxUnique label={`Alterar senha`} checked={updatePassword} change={setUpdatePassword} />
                {updatePassword &&
                    <div className="row">
                        <div className="col-6">
                            <Input type={`password`} label={`Nova senha`} value={password} change={setPassword} autoComplete={`new-password`} />
                            <PasswordChecklist password={password} confirmPassword={confirmPassword} onCriteriaChange={handleCriteriaChange} />
                        </div>
                        <div className="col-6">
                            <Input type={`password`} label={`Confirme a senha`} value={confirmPassword} change={setConfirmPassword} />
                        </div>
                    </div>
                }
            </div>
        </Modal>
    );
};

export default ModalMyProfile;
